import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import React, { useState, useMemo, useRef } from 'react';
import { MdOutlineArrowUpward } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';

import styles from './index.module.scss';
import OperationItem from './OperationItem';

import actions from '@/actions';
import EmptyList from '@/components/ui/EmptyList';
import { FieldText } from '@/components/ui/kit/Field';
import Select from '@/components/ui/kit/Select';
import LabelGroup from '@/components/ui/LabelGroup';
import Spinner from '@/components/ui/Spinner';
import API from '@/helpers/API';
import { NotifierService as notifier } from '@/libs/Notifier';
import { download } from '@/utils/FileDownloader';

const cx = classNames.bind(styles);
dayjs.extend(duration);

const SideBar = () => {
  const dispatch = useDispatch();
  const robots = useSelector((state) => state.robots);
  const query = useSelector((state) => state.query);

  const [loading, setLoading] = useState({
    operations: false,
    query: false,
    tlog: false,
  });
  const [operations, setOperations] = useState();
  const [isShowOperationList, setShowOperationList] = useState(false);
  const [targetOperationId, setTargetOperationId] = useState();
  const [messages, setMessages] = useState([]);

  // 시간 입력 필드 참조
  const fromTimeRef = useRef();
  const toTimeRef = useRef();

  useUpdateEffect(() => {
    loadOperations(query.robotId);

    // 초기화
    setTargetOperationId();
    setMessages([]);

    dispatch(actions.query.set('fromTime', ''));
    dispatch(actions.query.set('toTime', ''));
    dispatch(actions.query.set('targetMessage', null));
  }, [query.robotId]);

  useUpdateEffect(() => {
    if (!targetOperationId) return;

    const { startedAt, endedAt } = operations.find(({ id }) => id === targetOperationId);
    const [fromDate, fromTime] = dayjs(startedAt).format('YYYYMMDD|HHmm').split('|');
    const [toDate, toTime] = dayjs(endedAt).format('YYYYMMDD|HHmm').split('|');

    dispatch(actions.query.set('fromDate', fromDate));
    dispatch(actions.query.set('fromTime', fromTime));
    dispatch(actions.query.set('toDate', toDate));
    dispatch(actions.query.set('toTime', toTime));

    // 초기화
    setMessages([]);
    dispatch(actions.query.set('targetMessage', null));
  }, [targetOperationId]);

  useUpdateEffect(() => {
    fromTimeRef.current.value = query.fromTime;
    toTimeRef.current.value = query.toTime;
  }, [query.fromTime, query.toTime]);

  const handleChange = (e) => {
    dispatch(actions.query.set(e.target.name, e.target.value));
  };

  const handleLoading = (key, value) => {
    setLoading((prev) => ({ ...prev, [key]: value }));
  };

  const loadOperations = async (robotId) => {
    handleLoading('operations', true);

    API.get(`/robots/${robotId}/operations`)
      .then(({ success, data }) => {
        if (success) {
          setOperations(data);
        } else {
          throw new Error();
        }
      })
      .catch((_e) => {
        setOperations();
        notifier.error('Failed to load operations.');
      })
      .finally(() => {
        handleLoading('operations', false);
      });
  };

  const selectOperation = (e) => {
    setShowOperationList(false);
    setTargetOperationId(e.currentTarget.dataset.operationId);
  };

  const validAll = () => {
    const from = dayjs(query.fromDate + query.fromTime);
    const to = dayjs(query.toDate + query.toTime);

    // 시작일시가 종료일시 이후인 경우
    if (from.isAfter(to)) {
      notifier.error('Start time must be before end time.');
      return false;
    }

    // const { startedAt, endedAt } = operations.find(({ id }) => id === targetOperationId);
    // const operationStart = dayjs(startedAt).startOf('minute');
    // const operationEnd = dayjs(endedAt).startOf('minute');

    // Operation 시간 유효범위 밖인 경우
    // if (from.isBefore(operationStart) || to.isAfter(operationEnd)) {
    //   notifier.error(`Time range must be within ${operationStart.format('HH:mm')} - ${operationEnd.format('HH:mm')}`);
    //   return false;
    // }

    // Operation 시간 범위 2시간 초과인 경우
    // if (to.diff(from, 'minute') > 120) {
    //   notifier.error('Time range cannot exceed 2 hours.');
    //   return false;
    // }

    return true;
  };

  const doQuery = () => {
    if (!validAll()) return;

    handleLoading('query', true);
    setMessages([]);
    dispatch(actions.query.set('targetMessage', null));

    // prettier-ignore
    const params = {
      from: dayjs(query.fromDate + query.fromTime).utc().format(),
      to: dayjs(query.toDate + query.toTime).utc().format(),
    };

    API.get(`/robots/${query.robotId}/telemetries`, { params }).then(({ success, data }) => {
      setMessages(success ? data : []);
      handleLoading('query', false);
    });
  };

  const downloadTLog = () => {
    if (!validAll()) return;

    handleLoading('tlog', true);

    // prettier-ignore
    const params = {
      from: dayjs(query.fromDate + query.fromTime).utc().format(),
      to: dayjs(query.toDate + query.toTime).utc().format(),
    };

    API.get(`/robots/${query.robotId}/tlog`, {
      responseType: 'blob',
      params,
    })
      .then(({ headers, data }) => {
        const filename = headers['content-disposition'].match(/filename="([^"]+)"/)?.[1];
        download(data, filename);

        notifier.success('TLog downloaded successfully.');
      })
      .catch((_e) => {
        notifier.error('Failed to download TLog.');
      })
      .finally(() => {
        handleLoading('tlog', false);
      });
  };

  const selectMessage = (message) => {
    dispatch(actions.query.set('targetMessage', message));
  };

  const getOperationDataset = (origin) => {
    const start = dayjs(origin.startedAt);
    const end = dayjs(origin.endedAt);
    const operationDuration = dayjs.duration(end.diff(start));

    return {
      id: origin.id,
      date: start.format('YYYY-MM-DD'),
      startedAt: start.format('HH:mm'),
      endedAt: end.format('HH:mm'),
      duration: `${operationDuration.hours()}h ${operationDuration.minutes()}m`,
      inFlight: !origin.finished,
    };
  };

  const isExistOperations = useMemo(() => {
    return operations?.length > 0;
  }, [operations]);

  const selectText = useMemo(() => {
    if (loading.operations) return 'Loading...';
    if (!isExistOperations) return 'No operations';
    return 'Select';
  }, [loading.operations, isExistOperations]);

  const targetOperationDataset = useMemo(() => {
    if (!targetOperationId) return;

    const found = operations.find(({ id }) => id === targetOperationId);
    return getOperationDataset(found);
  }, [targetOperationId]);

  return (
    <div className={cx('container')}>
      <div className={cx('optionPanel')}>
        <LabelGroup label="Robot">
          <Select name="robotId" value={query.robotId} onChange={handleChange} className={cx('select')}>
            {robots.map((robot) => (
              <option key={robot.id} value={robot.id}>
                {robot.name}
              </option>
            ))}
          </Select>
        </LabelGroup>
        <LabelGroup label="Operation">
          {/* Select */}
          <div
            className={cx(['operationSelect', { disabled: !isExistOperations }])}
            onClick={() => isExistOperations && setShowOperationList(true)}>
            {selectText}
          </div>
          {/* List */}
          {isShowOperationList && operations && (
            <div className={cx('operationList')}>
              {operations
                .map((operation) => getOperationDataset(operation))
                .map((dataset) => (
                  <div key={dataset.id} data-operation-id={dataset.id} className={cx('row')} onClick={selectOperation}>
                    <OperationItem dataset={dataset} />
                  </div>
                ))}
            </div>
          )}
          {/* Target */}
          {targetOperationDataset && !isShowOperationList && (
            <div className={cx('targetOperation')} onClick={() => setShowOperationList(true)}>
              <OperationItem dataset={targetOperationDataset} />
            </div>
          )}
        </LabelGroup>
        <div className={cx('row')}>
          <LabelGroup label="Start Time">
            <FieldText
              ref={fromTimeRef}
              name="fromTime"
              disabled={!targetOperationId}
              defaultValue={query.fromTime}
              maxLength={4}
              placeholder="HHmm"
              onBlur={handleChange}
              className={cx('field')}
            />
          </LabelGroup>
          <LabelGroup label="End Time">
            <FieldText
              ref={toTimeRef}
              name="toTime"
              disabled={!targetOperationId}
              defaultValue={query.toTime}
              maxLength={4}
              placeholder="HHmm"
              onBlur={handleChange}
              className={cx('field')}
            />
          </LabelGroup>
        </div>
        <button className={cx(['button', 'accent'])} disabled={!targetOperationId || loading.query} onClick={doQuery}>
          {loading.query ? <Spinner /> : 'Query'}
        </button>
        <button
          className={cx(['button', 'accent'])}
          disabled={!targetOperationId || loading.tlog}
          onClick={downloadTLog}>
          {loading.tlog ? <Spinner /> : 'Download TLog'}
        </button>
      </div>
      {messages.length === 0 && (
        <div className={cx('empty')}>
          <EmptyList className={cx('message')} />
          <div className={cx('pleaseWrapper')}>
            <div className={cx('arrow')}>
              <MdOutlineArrowUpward size={16} color="gray" />
            </div>
            <div className={cx('please')}>Please query to view the messages.</div>
          </div>
        </div>
      )}
      {messages.length > 0 && (
        <div className={cx('messageList')}>
          <div className={cx('listInner')}>
            {messages.map((message) => (
              <div
                key={message.msgId}
                className={cx(['row', { selected: message.msgId === query.targetMessage?.msgId }])}
                onClick={() => selectMessage(message)}>
                {message.msgName} ({message.msgId})
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SideBar;
